"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { createCrudFormError, normalizeCrudServerError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import {
  type CargoFormValues,
  buildCargoFields,
  buildCargoFormSchema,
  buildCargoFormValues,
  buildCargoGroups,
  buildCargoPayload,
} from '../../../../lib/cargoForm'

type EditCargoPageProps = {
  params: {
    id: string
  }
}

type CargoDetail = Parameters<typeof buildCargoFormValues>[0] & {
  id: string
  canManage?: boolean
}

export default function EditCargoPage({ params }: EditCargoPageProps) {
  const t = useT()
  const router = useRouter()
  const cargoId = params.id
  const [cargo, setCargo] = React.useState<CargoDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const fields = React.useMemo(() => buildCargoFields(t), [t])
  const groups = React.useMemo(() => buildCargoGroups(t), [t])
  const schema = React.useMemo(() => buildCargoFormSchema(t), [t])

  React.useEffect(() => {
    let mounted = true

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await apiCall<CargoDetail>(`/api/freight/cargo/${cargoId}`)
        if (!response.ok || !response.result) {
          throw new Error(t('cargo.pages.edit.loadError', 'Failed to load cargo.'))
        }
        if (!mounted) return
        setCargo(response.result)
      } catch (loadError) {
        if (!mounted) return
        setError(loadError instanceof Error ? loadError.message : t('cargo.pages.edit.loadError', 'Failed to load cargo.'))
      } finally {
        if (mounted) setLoading(false)
      }
    }

    void load()
    return () => {
      mounted = false
    }
  }, [cargoId, t])

  return (
    <Page>
      <PageBody>
        {loading ? <LoadingMessage label={t('cargo.pages.edit.loading', 'Loading cargo…')} /> : null}
        {error ? <ErrorMessage label={error} /> : null}

        {!loading && !error && cargo?.canManage ? (
          <CrudForm<CargoFormValues>
            title={t('cargo.pages.edit.title', 'Edit cargo')}
            backHref={`/backend/cargo/${cargoId}`}
            cancelHref={`/backend/cargo/${cargoId}`}
            submitLabel={t('cargo.pages.edit.submit', 'Save changes')}
            fields={fields}
            groups={groups}
            initialValues={buildCargoFormValues(cargo)}
            schema={schema}
            onSubmit={async (values) => {
              const payload = buildCargoPayload(values)
              try {
                await updateCrud<{ id?: string }>(`freight/cargo/${cargoId}`, payload, {
                  errorMessage: t('cargo.pages.edit.saveError', 'Failed to update cargo.'),
                })
              } catch (err) {
                const normalized = normalizeCrudServerError(err)
                throw createCrudFormError(
                  normalized.message ?? t('cargo.pages.edit.saveError', 'Failed to update cargo.'),
                  normalized.fieldErrors,
                )
              }
              flash(t('cargo.pages.edit.success', 'Cargo updated successfully.'), 'success')
              router.push(`/backend/cargo/${cargoId}`)
            }}
          />
        ) : null}

        {!loading && !error && cargo && !cargo.canManage ? (
          <ErrorMessage label={t('cargo.pages.edit.ownerOnly', 'Only the cargo creator can edit this record.')} />
        ) : null}
      </PageBody>
    </Page>
  )
}
